import { DbgPaths, Paths } from "#/utils/Paths"
import { useTelemedicineAccess } from "@internal/telemedicine"
import { parseBookingEntryPayment } from "@internal/constants"
import { useMemberProgramFlags } from "#/services/user/hooks/useMemberProgram"
import React from "react"
import { Redirect, useLocation } from "react-router-dom"

// Program-agnostic entry for a generic "Book appointment" CTA: asks the backend which programs the
// member has and dispatches — none → home, single → that flow, multiple → the chooser. Program-
// specific entry points deep-link to the flow with `?program=` and skip this.
export function TelemedicineResolverPage(): JSX.Element | null {
  const { state, search } = useLocation()
  const paymentUrlParam = new URLSearchParams(search).get("payment")
  const payment = parseBookingEntryPayment(paymentUrlParam)
  const missingPayment = payment === undefined
  const memberProgram = useMemberProgramFlags(missingPayment)
  const { data: access, isLoading, isError } = useTelemedicineAccess()

  // A failed access read leaves us with no basis to dispatch — send the member home rather than
  // render a blank page.
  if (isError || (missingPayment && memberProgram.isError)) {
    return <Redirect to={Paths.HOME} />
  }

  if (isLoading || !access || (missingPayment && !memberProgram.data)) {
    return null
  }

  const { entry } = access

  // No programs, or the member's one program is one they aren't qualified for → home.
  if (entry.status === "none" || entry.status === "single_unqualified") {
    return <Redirect to={Paths.HOME} />
  }

  if (missingPayment && memberProgram.data?.isConsumer === false) {
    return <Redirect to={DbgPaths.CARROT_CLINIC} />
  }

  if (entry.status === "single_qualified") {
    const params = new URLSearchParams(search)
    params.set("program", entry.program)
    return <Redirect to={{ pathname: Paths.TELEMEDICINE_BOOKING, search: `?${params}`, state }} />
  }

  // multiple
  return (
    <Redirect
      to={{
        pathname: Paths.PROVIDER_FINDER,
        search: payment ? new URLSearchParams({ payment }).toString() : ""
      }}
    />
  )
}
